/**
 * Birth-Time Rectification page — birth-data input plus a list of
 * known life events (engagement, job-start, child-birth, etc.) that
 * the engine uses to triangulate the rectified TOB.
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { RectificationEvent, RectificationResponse } from '@/api/types';
import { useLang } from '@/i18n/useLang';

const _EVENT_TYPES: RectificationEvent['event_type'][] = [
  'engagement', 'marriage', 'job_start', 'child_birth',
  'education_milestone', 'accident', 'parent_loss', 'other',
];

const _EVENT_LABELS_HI: Record<string, string> = {
  engagement: 'सगाई',
  marriage: 'विवाह',
  job_start: 'नौकरी आरंभ',
  child_birth: 'संतान जन्म',
  education_milestone: 'शिक्षा मील-पत्थर',
  accident: 'दुर्घटना',
  parent_loss: 'माता/पिता का देहांत',
  other: 'अन्य',
};
const _EVENT_LABELS_EN: Record<string, string> = {
  engagement: 'Engagement',
  marriage: 'Marriage',
  job_start: 'Job start',
  child_birth: 'Child birth',
  education_milestone: 'Education milestone',
  accident: 'Accident',
  parent_loss: 'Loss of parent',
  other: 'Other',
};

export function RectifyPage({ onBack }: { onBack: () => void }) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [estimatedTob, setEstimatedTob] = useState('12:00:00');
  const [placeQuery, setPlaceQuery] = useState('');
  const [windowMin, setWindowMin] = useState(30);
  const [events, setEvents] = useState<RectificationEvent[]>([
    { event_type: 'job_start', date: '' },
  ]);

  const reportMut = useMutation<RectificationResponse, Error>({
    mutationFn: async () => {
      return api.rectify({
        name: name || 'Anonymous',
        dob,
        estimated_tob:
          estimatedTob.length === 5 ? `${estimatedTob}:00` : estimatedTob,
        place: { query: placeQuery },
        events: events.filter((e) => e.date),
        search_window_min: windowMin,
      });
    },
  });

  const ready = dob && estimatedTob && placeQuery && events.some((e) => e.date);

  const submitError =
    reportMut.error instanceof ApiError
      ? reportMut.error.detail
      : reportMut.error?.message;

  const updateEvent = useCallback(
    (i: number, patch: Partial<RectificationEvent>) => {
      setEvents((prev) => prev.map((e, j) => (j === i ? { ...e, ...patch } : e)));
    },
    [],
  );

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <button
        type="button" onClick={onBack}
        className="self-start text-xs text-saffron-300 hover:underline"
      >
        ← {t('मेन्यू पर वापस', 'Back to menu')}
      </button>
      <header className="rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <h1 className="text-lg font-semibold text-saffron-100">
          🎯 {t('जन्म-समय शुद्धिकरण', 'Birth-Time Rectification')}
        </h1>
        <p className="mt-1 text-xs text-stone-400">
          {t(
            '3-5 ज्ञात जीवन-घटनाओं के साथ अनुमानित समय को परिष्कृत करें।',
            'Refine your estimated birth time using 3–5 known life events.',
          )}
        </p>
      </header>

      <section className="grid gap-3 rounded-2xl bg-stone-900/60 p-4 ring-1 ring-stone-800 sm:grid-cols-2">
        <Field label={t('नाम', 'Name')} value={name} onChange={setName} />
        <Field
          label={t('जन्म तिथि', 'Date of birth')} type="date"
          value={dob} onChange={setDob} required
        />
        <Field
          label={t('अनुमानित समय', 'Estimated time')} type="time"
          value={estimatedTob} onChange={setEstimatedTob} required
        />
        <Field
          label={t('स्थान', 'Place')} value={placeQuery}
          onChange={setPlaceQuery}
          placeholder={t('जैसे: जयपुर, भारत', 'e.g. Jaipur, India')} required
        />
        <Field
          label={t('खोज-विंडो (मिनट)', 'Search window (min)')} type="number"
          value={String(windowMin)}
          onChange={(v) => setWindowMin(Number(v) || 30)}
        />
      </section>

      <section className="rounded-2xl bg-stone-900/60 p-4 ring-1 ring-stone-800">
        <h3 className="mb-2 text-sm font-semibold text-saffron-300">
          {t('जीवन की ज्ञात घटनाएँ', 'Known life events')}
        </h3>
        <p className="mb-3 text-xs text-stone-400">
          {t(
            'जितनी अधिक तथ्यपूर्ण घटनाएँ, उतना सटीक रेक्टिफिकेशन।',
            'The more factual events, the more accurate the rectification.',
          )}
        </p>
        <div className="space-y-2">
          {events.map((ev, i) => (
            <div
              key={i}
              className="grid gap-2 rounded-md bg-stone-950/50 p-2 sm:grid-cols-[1fr_1fr_auto]"
            >
              <select
                value={ev.event_type}
                onChange={(e) =>
                  updateEvent(i, {
                    event_type: e.target.value as RectificationEvent['event_type'],
                  })
                }
                className="rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100"
              >
                {_EVENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {lang === 'hi' ? _EVENT_LABELS_HI[type] : _EVENT_LABELS_EN[type]}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={ev.date}
                onChange={(e) => updateEvent(i, { date: e.target.value })}
                className="rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100"
              />
              <button
                type="button"
                onClick={() =>
                  setEvents((prev) => prev.filter((_, j) => j !== i))
                }
                className="rounded-md px-2 py-1 text-xs text-rose-300 hover:bg-rose-950/40"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setEvents((prev) => [...prev, { event_type: 'job_start', date: '' }])
            }
            className="text-xs text-saffron-300 hover:underline"
          >
            + {t('और घटना जोड़ें', 'Add another event')}
          </button>
        </div>
      </section>

      <button
        type="button"
        disabled={!ready || reportMut.isPending}
        onClick={() => reportMut.mutate()}
        className="rounded-md bg-saffron-600 px-4 py-2 font-semibold text-stone-950 transition hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
      >
        {reportMut.isPending
          ? t('शुद्धिकरण हो रहा है…', 'Rectifying…')
          : t('शुद्धिकरण करें', 'Rectify')}
      </button>

      {submitError && (
        <p className="rounded-2xl bg-rose-950/60 p-3 text-rose-200 ring-1 ring-rose-900">
          {submitError}
        </p>
      )}

      {reportMut.data && <RectifyResult report={reportMut.data} lang={lang} t={t} />}
    </div>
  );
}

function Field({
  label, value, onChange, type = 'text', placeholder, required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wider text-saffron-400">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100"
      />
    </div>
  );
}

function RectifyResult({
  report, lang, t,
}: {
  report: RectificationResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <article className="space-y-4">
      <section className="rounded-2xl bg-gradient-to-br from-saffron-900/60 to-stone-900 p-6 ring-2 ring-saffron-700/60">
        <p className="text-xs uppercase tracking-wider text-saffron-300">
          {t('शुद्धिकृत समय', 'Rectified TOB')}
        </p>
        <p className="mt-1 text-3xl font-mono font-bold text-saffron-100">
          {report.rectified_tob}
        </p>
        <p className="mt-1 text-sm text-stone-300">
          {t('अनुमानित से', 'estimated')}: {report.estimated_tob}
        </p>
        <p className="mt-2 text-sm text-stone-100">
          {t('विश्वास-स्तर', 'Confidence')}:{' '}
          <span className="font-semibold uppercase">{report.confidence}</span>
        </p>
        <p className="mt-3 text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.summary_hi : report.summary_en}
        </p>
      </section>

      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h3 className="mb-3 text-base font-semibold text-saffron-200">
          {t('शीर्ष विकल्प', 'Top Candidates')}
        </h3>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-saffron-300">
              <th className="px-2 py-1 text-left">{t('समय', 'Time')}</th>
              <th className="px-2 py-1 text-right">{t('स्कोर', 'Score')}</th>
              <th className="px-2 py-1 text-right">{t('मेल', 'Matched')}</th>
            </tr>
          </thead>
          <tbody>
            {report.top_candidates.map((c, i) => (
              <tr key={i} className="border-t border-stone-800">
                <td className="px-2 py-1 font-mono text-stone-100">{c.tob}</td>
                <td className="px-2 py-1 text-right tabular-nums text-stone-300">
                  {c.score.toFixed(2)}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-stone-300">
                  {c.matched_events}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <p className="text-center text-[10px] text-stone-600">
        {report.rules_version}
      </p>
    </article>
  );
}
